/* eslint-disable react-hooks/exhaustive-deps */
import { Drawer, message } from 'antd';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SearchFilters, Header, DrawerHeader } from 'datastake-daf/dist/components';

import {
  mapPrograms,
  selectFiltersConfig as defaultSelectFiltersConfig,
  filtersConfig,
  checkboxConfig,
  getProgramFilterOptions,
} from './helper';
import AmpCreate from './create';
import getColumns from './columns';
import Loading from '../../Loading';
import Styles from '../locations/style';
import DafTable from '../../Table/newTable';
import TablePagination from '../../Table/pagination';
import { DownloadOutlined } from '@ant-design/icons';
import { PROJECTS } from '../../../helpers/CanAccess';
import { displayMessage } from '../../../helpers/Messages';
import { getProject } from '../../../services/BaseService';
import { actions } from '../../../store/reducers/programs';
import { CREATE_DRAWER_WIDTH } from '../../../helpers/form';
import { canViewFeatureAction } from '../../../constants/roles';
import { renderBreadCrumbs } from '../../../helpers/breadCrumbs';
import { filterParams, useFilters } from '../../../helpers/SubjectSearch';

function Programs({
  title,
  drawerTitle,
  module,
  view,
  programs: { data, loading },
  programsGet,
  programsFormReset,
  programsRemove,
  programsAccept,
  programsDecline,
  authentication: { user, selectedProject, sourceId }
}) {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectOptions, setSelectOptions] = useState({
    country: [],
    category: [],
    subCategory: [],
    positionInTheMineralSupplyChain: []
  });

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    setPagination,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
  } = useFilters({
    selectFiltersConfig: defaultSelectFiltersConfig,
    view,
    module,
    filtersConfig,
  });

  const goTo = useNavigate();
  const [expandedRows, setExpandedRows] = useState([]);

  const expandedData = useMemo(() => {
    if (data.programs && Array.isArray(data.programs) && expandedRows.length) {
      return expandedRows.map((id) => data.programs.find((s) => s.id === id && s.children)).filter(s => s);
    }
  }, [expandedRows, data]);

  const deleteProgram = useCallback((id) => {
    programsRemove(id, () => {
      setActiveFilters({})
      setPagination((prev) => ({ ...prev, current: 1 }));
      programsGet(filterParams({
        project,
        module,
        view: 'programs',
        status: 'all',
        sourceId,
        activeFilters: {},
      }));
      message.success(t('Program deleted successfully '))
    });
  }, [programsRemove]);

  const approveProgram = useCallback((id) => {
    programsAccept(id, () => {
      setActiveFilters({})
      setPagination((prev) => ({ ...prev, current: 1 }));
      programsGet(filterParams({
        project,
        module,
        view: 'programs',
        status: 'all',
        sourceId,
        activeFilters: {},
      }));
      message.success(t('Program approved'))
    });
  }, [programsAccept]);


  const rejectProgram = useCallback((id) => {
    programsDecline(id, () => {
      setActiveFilters({})
      setPagination((prev) => ({ ...prev, current: 1 }));
      programsGet(filterParams({
        project,
        module,
        view: 'programs',
        status: 'all',
        sourceId,
        activeFilters: {},
      }));
      message.success(t('Program rejected'))
    });
  }, [programsDecline]);

  const columns = useMemo(() =>
    getColumns(
      t,
      module,
      selectedProject !== PROJECTS.myData ? 'project' : 'show',
      sourceId,
      expandedData,
      activeFilters,
      goTo,
      deleteProgram,
      approveProgram,
      rejectProgram
    ), [
    t, module, view, selectedProject, sourceId, expandedData,
    activeFilters, deleteProgram, approveProgram, rejectProgram
  ]);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [editVisible, setEditVisible] = useState(params.has('create'));
  const [programs, setPrograms] = useState([]);

  const project = getProject();

  useEffect(() => {
    if (sourceId) {
      programsGet(filterParams({
        project,
        module,
        view: 'programs',
        status: 'all',
        sourceId,
        activeFilters,
      }));
    }
  }, [user.language, selectedProject, sourceId, activeFilters])

  useEffect(() => {
    if (data.programs) {
      setPrograms(mapPrograms(data.programs, data.programsOptions))
    }

    setSelectOptions((prev) => ({
      ...prev,
      ...getProgramFilterOptions(data.programsOptions),
    }));

    if (typeof data.totalItems === 'number') {
      setPagination((prev) => ({ ...prev, total: data.totalItems }));
    }
  }, [data.programs]);

  const canEdit = (canViewFeatureAction({ user, module, feature: 'stakeholders', action: 'create', view }) ||
    canViewFeatureAction({ user, module, feature: 'stakeholders', subfeature: 'all', action: 'create', view })) && selectedProject === PROJECTS.myData;

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  const breadCrumbs = useMemo(() => {
    return renderBreadCrumbs({ t, view: 'programs', goTo, mod: module });
  }, [view, t, goTo]);

  return (
    <Styles className="semibold form-input-output">
      {loading && <Loading />}
      <Header
        title={t(title)}
        breadcrumbs={breadCrumbs}
        actionButtons={canEdit ? [
          {
            label: t('New'),
            onClick: () => setEditVisible(true),
            type: 'primary',
          },
          {
            onClick: () => { },
            disabled: true,
            icon: <DownloadOutlined />,
          },
        ] : []}
      />
      <div className="column start table-content w-support-text">
        <div className='mt-6 ml-6 mr-6'>
          <SearchFilters
            t={t}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            hasError={hasError}
            setHasError={setHasError}
            filtersConfig={newFiltersConfig}
            checkboxConfig={checkboxConfig}
            onSearch={onSearch}
            activeFilters={activeFilters}
            canClear={canClearSearch}
            clearFilters={() => setActiveFilters({})}
          />
        </div>
        <div className='daf-table-wrapper pagination-w-padding'>
          <DafTable
            columns={columns}
            data={programs}
            pagination={pagination}
            sourcesKey="authorId"
            rowKey="dbId"
            doEmptyRows
            selectOptions={selectOptions}
            filtersConfig={defaultSelectFiltersConfig}
            onFilterChange={onFiltersChange}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            defaultFilters={defaultFilters}
            onChange={onTableChange}
            expandable={{
              onExpandedRowsChange: (expanded) => setExpandedRows(expanded),
              expandedRowClassName: () => 'expanded-row',
            }}
          />
          <TablePagination
            t={t}
            page={pagination.current}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={pagination.total}
            doTotalItems
            onChangePagination={(val) => {
              onTableChange({ ...pagination, current: 1, pageSize: val });
            }}
            perPage={pagination.pageSize}
            arrowIcons
          />
        </div>
      </div>
      <Drawer
        destroyOnClose={true}
        title={<DrawerHeader title={t(drawerTitle)} />}
        open={editVisible}
        maskClosable={false}
        onClose={() => {
          setEditVisible(false);
          goTo(`/app/${module}/${view}`)
          programsGet(filterParams({
            project,
            module,
            // TO-DO change to view
            view: 'scp',
            status: 'all',
            sourceId,
            activeFilters,
          }));
          programsFormReset({ module, view: [view, 'new'] });
        }}
        width={CREATE_DRAWER_WIDTH}
        bodyStyle={{ padding: 0 }}>
        {editVisible ? <AmpCreate
          showSaveAndNext={false}
          namespace="programs"
          module={module}
          view={['programs', "new"]}
          edit={false}
          onSubmitted={(type, m, payload) => {
            displayMessage(type, m);
            setEditVisible(false);
            goTo(`/app/${module}/edit/${view}/${payload.datastakeId}`)
          }}
          onCancel={() => setEditVisible(false)} /> : null}
      </Drawer>
    </Styles >
  )
};

const mstp = (state) => ({ programs: state.programs, authentication: state.authentication });

export default connect(mstp, actions)(Programs);
